import {
  type SegmentState,
  deriveScaleRecommendationState,
  selectActiveDatabasesByOrgAsList,
  selectAppsByOrgAsList,
} from "@app/deploy";
import { useSelector as useAppSelector } from "@app/react";
import { type WebState, schema } from "@app/schema";
import { useMemo } from "react";

export interface ContainerEfficiencyItem {
  segmentState: SegmentState;
}

function toSegmentState(
  policy: { scalingEnabled: boolean } | undefined,
  recState: "scale_down" | "scale_up" | "none",
): SegmentState {
  if (policy?.scalingEnabled) return "autoscaled";
  if (recState === "scale_down") return "scale_down";
  if (recState === "scale_up") return "scale_up";
  return "rightsized";
}

/** Databases don't have an "autoscaled" option; treat as rightsized. */
function toSegmentStateForDb(
  _policy: { scalingEnabled: boolean } | undefined,
  recState: "scale_down" | "scale_up" | "none",
): SegmentState {
  if (recState === "scale_down") return "scale_down";
  if (recState === "scale_up") return "scale_up";
  return "rightsized";
}

export function useDatabasesByOrgWithState(): ContainerEfficiencyItem[] {
  const databases = useAppSelector((s: WebState) =>
    selectActiveDatabasesByOrgAsList(s),
  );
  const services = useAppSelector(schema.services.selectTable);
  const recommendations = useAppSelector(
    schema.manualScaleRecommendations.selectTableAsList,
  );
  const serviceSizingPolicies = useAppSelector(
    schema.serviceSizingPolicies.selectTable,
  );

  return useMemo(() => {
    const items: ContainerEfficiencyItem[] = [];
    for (const db of databases) {
      const service = schema.services.findById(services, {
        id: db.serviceId,
      });
      const policy = service
        ? serviceSizingPolicies[service.serviceSizingPolicyId]
        : undefined;
      const rec = recommendations.find((r) => r.serviceId === db.serviceId);
      const serviceForRecEval = service
        ? {
            id: service.id,
            instanceClass: service.instanceClass,
            containerMemoryLimitMb: service.containerMemoryLimitMb,
            serviceSizingPolicyId: service.serviceSizingPolicyId,
          }
        : undefined;
      const recState = deriveScaleRecommendationState(
        serviceForRecEval,
        policy,
        rec,
      );
      items.push({
        segmentState: toSegmentStateForDb(policy, recState),
      });
    }
    return items;
  }, [databases, services, recommendations, serviceSizingPolicies]);
}

export function useAppServicesByOrgWithState(): ContainerEfficiencyItem[] {
  const services = useAppSelector(schema.services.selectTableAsList);
  const orgApps = useAppSelector((s: WebState) => selectAppsByOrgAsList(s));
  const recommendations = useAppSelector(
    schema.manualScaleRecommendations.selectTableAsList,
  );
  const serviceSizingPolicies = useAppSelector(
    schema.serviceSizingPolicies.selectTable,
  );

  const orgAppIds = useMemo(() => new Set(orgApps.map((a) => a.id)), [orgApps]);

  return useMemo(() => {
    const items: ContainerEfficiencyItem[] = [];
    for (const service of services) {
      if (!service.appId || !orgAppIds.has(service.appId)) continue;
      const policy = serviceSizingPolicies[service.serviceSizingPolicyId];
      const rec = recommendations.find((r) => r.serviceId === service.id);
      const recState = deriveScaleRecommendationState(
        {
          id: service.id,
          instanceClass: service.instanceClass,
          containerMemoryLimitMb: service.containerMemoryLimitMb,
          serviceSizingPolicyId: service.serviceSizingPolicyId,
        },
        policy,
        rec,
      );
      items.push({
        segmentState: toSegmentState(policy, recState),
      });
    }
    return items;
  }, [services, orgAppIds, recommendations, serviceSizingPolicies]);
}
